import Emitter from '@resources/js/helpers/Emitter'
import {
	startCheckAvail,
	getCheckAvailStatus,
	fetchAvailResult,
} from '@resources/js/components/check-avail/avail.service'

const ERROR_MESSAGES = {
	noCheckId:
		'Check availability ID is not set. Please start check availability first.',
	startAvail: 'Error in startAvail:',
	checkStatus: 'Error in checkAvailStatus:',
	getResult: 'Error in getAvailResult:',
	restartFailed:
		'Check availability session restart failed: status did not finish',
}

export const CHECK_AVAIL_EVENTS = {
	START: 'avail-start',
	FINISH: 'avail-finish',
}

export default class CheckAvail extends Emitter {
	static #instance
	#checkAvailId = null
	#checkAvailStatusFinished = false
	#checkAvailResult = {}
	// номер останнього refetch — відповіді старіших запитів відкидаємо
	#lastRefetchId = 0

	constructor() {
		super()

		if (CheckAvail.#instance) return CheckAvail.#instance

		CheckAvail.#instance = this
	}

	async init(params = {}) {
		this.emit(CHECK_AVAIL_EVENTS.START)

		try {
			this.#checkAvailResult = (await this.#startAndFetchResult(params)) ?? {}
		} finally {
			this.emit(CHECK_AVAIL_EVENTS.FINISH)
		}
	}

	// повертає результат або null, якщо статус перевірки так і не завершився
	async #startAndFetchResult(params = {}) {
		const startAvailData = await this.#getStartAvail(params)
		this.#checkAvailId = startAvailData.id

		this.#checkAvailStatusFinished = await this.#getCheckAvailStatusFinished()

		if (!this.#checkAvailStatusFinished) return null

		return this.getAvailResult(params)
	}

	get availableHotels() {
		return this.#checkAvailResult.hotels || []
	}

	get filters() {
		return this.#checkAvailResult.filters || {}
	}

	get totalHotelsCount() {
		return this.#checkAvailResult.total || 0
	}

	get totalHotelsCountWithoutFilters() {
		return this.#checkAvailResult.totalWithoutFilters || 0
	}

	// params має бути повним набором як для init() (базові search-параметри
	// + фільтри), не тільки фільтри — гілка "сесія мертва" робить повний
	// restart через #startAndFetchResult(params)
	// повертає false, якщо поки запит ішов, стартував новіший — його відповідь застаріла
	async refetch(params = {}) {
		const refetchId = ++this.#lastRefetchId
		this.emit(CHECK_AVAIL_EVENTS.START)

		try {
			const sessionAlive = await this.#getCheckAvailStatusFinished()

			const result = sessionAlive
				? await this.getAvailResult(params)
				: await this.#startAndFetchResult(params)

			if (refetchId !== this.#lastRefetchId) return false

			// рестарт сесії не вдався — це помилка, а не успіх зі старим списком
			if (!result) throw new Error(ERROR_MESSAGES.restartFailed)

			this.#checkAvailResult = result
			return true
		} finally {
			// лоадер ховаємо лише після останнього запиту
			if (refetchId === this.#lastRefetchId)
				this.emit(CHECK_AVAIL_EVENTS.FINISH)
		}
	}

	async #getStartAvail(params = {}) {
		try {
			const startAvailData = await startCheckAvail(params)
			return startAvailData
		} catch (err) {
			console.error(ERROR_MESSAGES.startAvail, err)
			throw err
		}
	}

	async #getCheckAvailStatusFinished() {
		if (!this.#checkAvailId) {
			console.error(ERROR_MESSAGES.noCheckId)
			return false
		}

		try {
			await getCheckAvailStatus(this.#checkAvailId)

			return true
		} catch (err) {
			console.error(ERROR_MESSAGES.checkStatus, err)

			return false
		}
	}

	async getAvailResult(params = {}) {
		try {
			const availResult = await fetchAvailResult(params)

			return availResult
		} catch (err) {
			console.error(ERROR_MESSAGES.getResult, err)
			throw err
		}
	}
}
